import type { QueryKey } from '@tanstack/react-query'

export type LookupTier = 'static' | 'reference' | 'standard' | 'volatile'

export const STALE_TIMES: Record<LookupTier, number> = {
  static: Infinity,
  reference: 60 * 60 * 1000,
  standard: 10 * 60 * 1000,
  volatile: 60 * 1000,
}

export const GC_TIME = 30 * 60 * 1000

// Kept out of localStorage because they answer "who is on staff right now". A guard
// deactivated elsewhere would otherwise still be offered for assignment after a reload:
// the restored list counts as fresh, so nothing refetches and no refresh, focus or
// navigation clears it until its stale time runs out.
export const UNPERSISTED_QUERY_KEY_PREFIXES: readonly (readonly unknown[])[] = [
  ['invitation-user-lookup'],
  ['employees', 'lookup', 'guards'],
  ['clients', 'lookup'],
  ['memo-book', 'entries'],
  ['chat'],
  ['pay-rates'],
  ['payroll'],
  ['office-time'],
  ['payslips'],
  ['bill-rates'],
  ['invoices'],
  ['invoice-templates'],
  ['incidents'],
  ['site-roster'],
  ['records-export'],
  ['memo-export'],
  ['incident-references'],
  // The plan and subscription state: a restored copy would show the previous billing state (and its
  // banner) until the query refetched.
  ['billing'],
  // The signed-in user's permission list: it is withdrawn and restored with the account's billing
  // state, so a restored copy would show the previous permission set until the query refetched.
  ['session', 'permissions'],
]

const keyHasPrefix = (key: readonly unknown[], prefix: readonly unknown[]): boolean =>
  prefix.every((segment, index) => key[index] === segment)

export const isUnpersistedQueryKey = (key: QueryKey): boolean =>
  UNPERSISTED_QUERY_KEY_PREFIXES.some((prefix) => keyHasPrefix(key as readonly unknown[], prefix))
